import React, { useRef } from "react";
import {
  getAudioData,
  getHeygenData,
  getCaptionedNewsReelData,
  resetArticle,
} from "../../Redux/Slice/DataSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NewsSidebar } from "../NewsList/Components/NewsSidebar";
import { Col, Drawer, Grid, Layout, Row, Spin, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileTextFilled,
  LoadingOutlined,
  MenuOutlined,
  VideoCameraFilled,
} from "@ant-design/icons";
import { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { StyledMainCol, StyledQuill } from "./styles";
import { StyledPrimaryButton } from "../ArticleGen/styles";
import NoPermission from "../../NoPermission";
import { useKeycloak } from "@react-keycloak/web";
import { HEYGEN_SPEAKERS } from "../../utils";

const { Content } = Layout;
const { Text, Title } = Typography;

const Link = Quill.import("formats/link");
Link.sanitize = (url) => {
  const hasProtocol = /^[a-zA-Z]+:\/\//.test(url);
  if (!hasProtocol) {
    url = "http://" + url;
  }
  let anchor = document.createElement("a");
  anchor.href = url;
  return anchor.href;
};
Quill.register(Link, true);

const Font = Quill.import("formats/font");
Font.whitelist = [
  "Arial",
  "sans-serif",
  "serif",
  "monospace",
  "times-new-roman",
];
Quill.register(Font, true);
const { useBreakpoint } = Grid;

export const NewsGen = () => {
  const dispatch = useDispatch();
  const [mediaType, setMediaType] = useState("video");
  const [videoModel, setVideoModel] = useState("avatar");
  const vidconfig = useSelector((state: any) => state.caster.selectedConfig);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [formattedText, setFormattedText] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [visible, setVisible] = useState(false);
  const screens = useBreakpoint();
  const showDrawer = () => setVisible(true);
  const closeDrawer = () => setVisible(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedNews = useSelector((state: any) => state.news.selectedArticles);

  // Get selected language from URL query param, fall back to localStorage
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    searchParams.get("language") || localStorage.getItem("selectedLanguage") || "English"
  );

  // Helper function to check if language is RTL
  const isRtlLanguage = (lang?: string) => {
    if (!lang) return false;
    const lower = lang.toLowerCase();
    return (
      lower.startsWith("ar") ||
      lower === "arabic" ||
      lower.startsWith("ur") ||
      lower === "urdu" ||
      lower.startsWith("pa") ||
      lower === "pashto"
    );
  };

  const { keycloak } = useKeycloak();

  const antIcon = (
    <LoadingOutlined
      style={{ fontSize: 30, color: "#005461", fontWeight: 600 }}
      spin
    />
  );

  const removeHtmlTags = (text) => {
    return text.replace(/<[^>]*>/g, "");
  };

  const handleBack = () => {
    setShowGenerate(false);
    setEditedText("");
    dispatch(resetArticle());
    localStorage.removeItem("newsText");
    navigate("/NewsList");
  };

  const generateMedia = () => {
    // Use language from URL query param (already normalized lowercase)
    const normalizedLang = selectedLanguage.toLowerCase();

    if (mediaType === "audio") {
      dispatch(
        getAudioData({
          text: removeHtmlTags(editedText),
          voice: vidconfig?.voice,
          token: keycloak.token,
          language: normalizedLang,
        })
      );
      return;
    }

    if (mediaType === "video") {
      if (videoModel === "slideshow") {
        dispatch(
          getCaptionedNewsReelData({
            content: removeHtmlTags(editedText),
            language: normalizedLang,
          })
        );
        return;
      }

      // ✅ HeyGen avatar video
      dispatch(
        getHeygenData({
          text: removeHtmlTags(editedText),
          avatar_id: vidconfig?.image, // ✅ avatar_id
          voice_id: vidconfig?.voice, // ✅ voice_id
          token: keycloak.token,
        })
      );
    }
  };

  const { generatedAudios } = useSelector(
    (state: any) => state?.data?.audioData
  );
  const { generatedVideos } = useSelector(
    (state: any) => state?.data?.videoData
  );
  const { loading: captionedNewsReelLoading } = useSelector(
    (state: any) => state.data.captionedNewsReelData
  );
  const lastAudioRef = useRef(null);
  const lastVideoRef = useRef(null);

  useEffect(() => {
    if (
      (generatedAudios.length > 0 &&
        lastAudioRef.current !== generatedAudios[generatedAudios.length - 1]) ||
      (generatedVideos.length > 0 &&
        lastVideoRef.current !== generatedVideos[generatedVideos.length - 1])
    ) {
      // New audio was added

      // Update the ref with the latest generated audio
      lastAudioRef.current = generatedAudios[generatedAudios.length - 1];
    }
  }, [
    generatedAudios,
    generatedVideos,
    dispatch,
    keycloak.token,
    keycloak.tokenParsed?.email,
  ]);

  const {
    response: chatResponse = [],
    loading: chatLoading,
    initialLoad: initLoad,
  } = useSelector((state: any) => state.data.newsData || {});

  useEffect(() => {
    const savedText = localStorage.getItem("newsText");
    if (savedText) {
      setEditedText(savedText);
      setFormattedText(savedText);
    }
  }, []);

  const prevNewsText = useRef(""); // Track previous text
  const timeoutRef = useRef(null); // Timeout to detect final article update

  useEffect(() => {
    if (chatResponse && chatResponse !== "null") {
      setEditedText(chatResponse);
    }
  }, [chatResponse]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (
        prevNewsText.current !== editedText &&
        editedText.length > 50 &&
        keycloak.authenticated &&
        keycloak.token
      ) {
        console.log("Article streaming complete!");
      }
      prevNewsText.current = editedText;
    }, 2000);

    return () => clearTimeout(timeoutRef.current);
  }, [editedText, keycloak.authenticated, keycloak.token, dispatch]);

  useEffect(() => {
    if (chatResponse) {
      setEditedText(chatResponse);
    }
  }, [chatResponse, setEditedText]);

  useEffect(() => {
    if (chatResponse && isRtlLanguage(selectedLanguage)) {
      setFormattedText(
        `<div style="direction: rtl; text-align: right; font-family: Arial;">${chatResponse}</div>`
      );
    }
  }, [chatResponse, selectedLanguage]);

  // Update selectedLanguage from localStorage when it changes
  useEffect(() => {
    const handleStorageChange = () => {
      const storedLanguage =
        localStorage.getItem("selectedLanguage") || "English";
      setSelectedLanguage(storedLanguage);
    };

    // Listen for storage changes
    window.addEventListener("storage", handleStorageChange);

    // Also check on mount and periodically (for same-tab updates)
    const interval = setInterval(() => {
      const storedLanguage =
        localStorage.getItem("selectedLanguage") || "English";
      if (storedLanguage !== selectedLanguage) {
        setSelectedLanguage(storedLanguage);
      }
    }, 100);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [selectedLanguage]);

  // Set Quill editor direction based on selected language
  useEffect(() => {
    const quillEditor = document.querySelector(
      "#quill-content-news .ql-editor"
    );
    if (quillEditor) {
      const isRtl = isRtlLanguage(selectedLanguage);
      quillEditor.setAttribute("dir", isRtl ? "rtl" : "ltr");
      (quillEditor as HTMLElement).style.textAlign = isRtl ? "right" : "left";
    }
  }, [selectedLanguage]);

  const isRTL = (text) => {
    const rtlCharPattern = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
    return rtlCharPattern.test(text);
  };

  useEffect(() => {
    if (editedText) {
      localStorage.setItem("newsText", editedText);
    } else if (`${formattedText}`) {
      localStorage.setItem("formateText", `${formattedText}`);
    }
  }, [editedText]);

  const handleChange = (value) => {
    setEditedText(value);
    setFormattedText(value);

    // Use selectedLanguage to determine direction
    const isRtl = isRtlLanguage(selectedLanguage);
    const editor = document.querySelector("#quill-content-news .ql-editor");
    if (editor) {
      editor.setAttribute("dir", isRtl ? "rtl" : "ltr");
      (editor as HTMLElement).style.textAlign = isRtl ? "right" : "left";
    }
  };

  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: Font.whitelist }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "video"],
      [{ direction: "rtl" }, { align: [] }],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "direction",
    "align",
  ];

  return keycloak.authenticated ? (
    <StyledMainCol lg={{ span: 18, offset: 3 }}>
      <Row
        justify={"space-between"}
        style={{ minHeight: "100%", borderRadius: "5px" }}
      >
        <Col lg={15} xs={24}>
          <Content
            style={{
              minHeight: "100%",
              maxHeight: "100%",
              background: "white",
              borderRadius: "10px",
              borderRight: "10px solid whitesmoke",
              marginLeft:
                !screens.lg && !screens.xl && !screens.xxl ? "10px" : "0",
            }}
          >
            {!screens.lg && !screens.xl && !screens.xxl && (
              <StyledPrimaryButton
                icon={<MenuOutlined />}
                type="primary"
                onClick={showDrawer}
                style={{
                  display: "block",
                  marginBottom: "5px",
                  borderRadius: "5px",
                  width: "100%",
                }}
              >
                Media Generation
              </StyledPrimaryButton>
            )}

            <Row
              align={"middle"}
              style={{ borderBottom: "0.5px solid lightgrey", height: "60px" }}
            >
              <Title
                style={{
                  fontSize: "18px",
                  marginLeft: "20px",
                  display: "flex",
                  alignItems: "center",
                  color: "#005461",
                }}
              >
                <FileTextFilled
                  style={{
                    fontSize: "14px",
                    marginRight: "5px",
                    color: "#005461",
                  }}
                />
                Generated News
              </Title>
            </Row>
            <Row
              style={{ width: "100%", marginTop: "30px", minHeight: "100%" }}
            >
              {initLoad ? (
                <Content style={{ marginTop: "250px" }}>
                  <Row justify={"center"} align={"middle"}>
                    <Spin style={{ fontSize: "30px" }} indicator={antIcon} />
                    <Text
                      style={{
                        fontSize: "30px",
                        marginLeft: 10,
                        color: "#005461",
                        fontFamily: "Lato",
                        fontWeight: 600,
                      }}
                    >
                      Generating News
                    </Text>
                  </Row>
                </Content>
              ) : (
                <Col span={24}>
                  <div
                    id="quill-content-news"
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      border: "1px solid #e1e8ed",
                      overflow: "hidden",
                    }}
                  >
                    <StyledQuill
                      value={
                        isRtlLanguage(selectedLanguage)
                          ? `${formattedText}`
                          : `${editedText}`
                      }
                      onChange={handleChange}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      defaultValue="<p style='font-family: Arial;'>Start typing...</p>"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Content>
        </Col>

        <Col
          lg={9}
          xs={0}
          style={{
            minHeight: "100%",
            background: "white",
            borderRadius: "5px",
          }}
        >
          <Row justify={"center"}>
            <NewsSidebar
              mediaType={mediaType}
              setMediaType={setMediaType}
              generateMedia={generateMedia}
              setUnsavedChanges={setUnsavedChanges}
              unsavedChanges={unsavedChanges}
              editedText={editedText}
              handleBack={handleBack}
              videoModel={videoModel}
              setVideoModel={setVideoModel}
            />
          </Row>
        </Col>

        <Drawer
          title={
            <>
              <VideoCameraFilled
                style={{
                  fontSize: "16px",
                  marginRight: "3px",
                  color: "#005461",
                }}
              />
              Media Sidebar
            </>
          }
          placement="right"
          onClose={closeDrawer}
          visible={visible}
        >
          <NewsSidebar
            mediaType={mediaType}
            setMediaType={setMediaType}
            generateMedia={generateMedia}
            setUnsavedChanges={setUnsavedChanges}
            unsavedChanges={unsavedChanges}
            editedText={editedText}
            handleBack={handleBack}
            videoModel={videoModel}
            setVideoModel={setVideoModel}
          />
        </Drawer>
      </Row>
    </StyledMainCol>
  ) : (
    <NoPermission />
  );
};

export default NewsGen;
